import React, { useEffect, useState } from "react";
import { Typography, Box, useTheme, Button } from "@mui/material";
import Header from "../../components/Header";
import RealTimeClock from "../../components/Time";
import UserImage from "../../components/UserImage";
import { cobotColours } from "../../theme/theme";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import moment from "moment";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import toast, { Toaster } from 'react-hot-toast';
import api from "../../services/api";

const EmployeeClockedIn = () => {
    const cobotTheme = useTheme();
    const colors = cobotColours(cobotTheme.palette);
    const { id } = useParams();
    const [data, setData] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [timeOnSite, setTimeOnSite] = useState("");
    const [activeBreaks, setActiveBreaks] = useState([]);
    const [totalBreakTimeFormatted, setTotalBreakTimeFormatted] = useState("0 hours 0 mins");
    const [timeWorkedFormatted, setTimeWorkedFormatted] = useState("0 hours 0 mins");
    const [currentActiveBreakDetails, setCurrentActiveBreakDetails] = useState(null);
    const [isBreakButtonLoading, setIsBreakButtonLoading] = useState(false);

    // Function to calculate and format time on site
    const calculateTimeOnSite = (clockInTime) => {
        if (!clockInTime) return { duration: moment.duration(0), formatted: "" };
        const now = moment();
        const clockedInAt = moment(clockInTime);
        const duration = moment.duration(now.diff(clockedInAt));
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();
        return { duration, formatted: `${hours} hours ${minutes} mins` };
    };

    const formatDuration = (durationMs) => {
        if (typeof durationMs !== 'number' || durationMs < 0) return "0 hours 0 mins";
        const duration = moment.duration(durationMs);
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();
        return `${hours} hours ${minutes} mins`;
    };

    const calculateTotalBreakDurationMs = (breaks) => {
        let totalBreakMs = 0;
        const now = moment();
        breaks.forEach(br => {
            const start = moment(br.breakStartTime);
            const end = br.breakEndTime ? moment(br.breakEndTime) : now;
            totalBreakMs += end.diff(start);
        });
        return totalBreakMs;
    };

    // Function to determine shift status and time difference
    const getShiftStatus = (shifts) => {
        if (!Array.isArray(shifts) || shifts.length === 0) return "No shift assigned";

        const now = moment();
        for (const shift of shifts) {
            const shiftDate = moment(shift.date).startOf('day');
            const shiftStart = moment(shift.startTime);
            const shiftEnd = moment(shift.endTime);

            // Ensure we are checking today's shift only
            if (!now.isSame(shiftDate, 'day')) continue;

            if (shiftStart.isBefore(now) && shiftEnd.isAfter(now)) {
                const timeLeft = moment.duration(shiftEnd.diff(now));
                return `Shift will end in ${timeLeft.hours()} hours ${timeLeft.minutes()} mins`;
            } else if (shiftEnd.isBefore(now)) {
                const timeSinceEnd = moment.duration(now.diff(shiftEnd));
                return `Shift ended ${timeSinceEnd.hours()} hours ${timeSinceEnd.minutes()} mins ago`;
            }
        }
        return "Not Clocked In";
    };

    // Format shift start and end times


    // Fetch employee details
    const getEmployeeDetails = async () => {
        try {
            const fetchedEmployeeData = await api.employee.getDetails(id);

            let clockInTimeToUse = location.state?.clockInTime;
            let latestClockInRecord = null;

            if (fetchedEmployeeData?.data) {
                if (!clockInTimeToUse) {
                    try {
                        const clockingData = await api.employee.getLatestClocking(id);
                        if (clockingData.success && clockingData.data) {
                            latestClockInRecord = clockingData.data;
                            clockInTimeToUse = clockingData.data.clockInTime;
                            setTimeOnSite(calculateTimeOnSite(clockInTimeToUse).formatted);

                            if (latestClockInRecord._id) {
                                fetchBreaksForClocking(latestClockInRecord._id);
                            }
                        } else {
                            setTimeOnSite("Not Clocked In");
                            setActiveBreaks([]);
                        }
                    } catch (clockingError) {
                        console.error("Error fetching latest clocking record:", clockingError);
                        setTimeOnSite("Error retrieving clock-in status");
                        setActiveBreaks([]);
                    }
                } else {                
                    setTimeOnSite(calculateTimeOnSite(clockInTimeToUse).formatted);
                     try {
                        const clockingData = await api.employee.getLatestClocking(id);
                        if (clockingData.success && clockingData.data) {
                            latestClockInRecord = clockingData.data; 
                            if (latestClockInRecord._id) {
                               fetchBreaksForClocking(latestClockInRecord._id);
                            }
                        }
                    } catch (error) {
                        console.error("Error fetching latest clocking for breaks when already clocked in:", error);
                        setActiveBreaks([]);
                    }
                }

                setData({ 
                    ...fetchedEmployeeData.data, 
                    latestClockIn: latestClockInRecord 
                });

            } else {
                console.error("Error: Invalid employee data format");
                setData(null); // Clear data or set to an error state
                setTimeOnSite("Error loading employee data");
                setActiveBreaks([]);
            }
        } catch (error) {
            console.error("Error fetching employee details:", error);
            setData(null);
            setTimeOnSite("Error loading employee data");
            setActiveBreaks([]);
        }
    };

    const fetchBreaksForClocking = async (clockingId) => {
        if (!clockingId) return;
        try {
            const breaksData = await api.shiftActivities.getBreaksByClocking(clockingId);
            if (breaksData.success) {
                setActiveBreaks(breaksData.data || []);
            } else {
                 setActiveBreaks([]);
            }
        } catch (breaksError) {
            console.error("Error fetching breaks:", breaksError);
            setActiveBreaks([]);
        }
    };

    useEffect(() => {
        getEmployeeDetails();
    }, [id, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let intervalId;
        let effectiveClockInTime = location.state?.clockInTime;

        if (!effectiveClockInTime && data?.latestClockIn?.clockInTime) {
            effectiveClockInTime = data.latestClockIn.clockInTime;
        }
        
        if (effectiveClockInTime) {
            const updateTimes = () => {
                const timeOnSiteResult = calculateTimeOnSite(effectiveClockInTime);
                setTimeOnSite(timeOnSiteResult.formatted);

                const totalBreakMs = calculateTotalBreakDurationMs(activeBreaks);
                setTotalBreakTimeFormatted(formatDuration(totalBreakMs));

                const timeWorkedMs = timeOnSiteResult.duration.asMilliseconds() - totalBreakMs;
                setTimeWorkedFormatted(formatDuration(Math.max(0, timeWorkedMs))); // Ensure non-negative
            };

            updateTimes(); // Initial call
            intervalId = setInterval(updateTimes, 60000); // Update every minute
        } else if (data && !effectiveClockInTime) {
            // This case means user just clocked in (no location.state.clockInTime)
            // and getEmployeeDetails fetched the new clock-in record (if that logic exists and is fast enough)
            // or if the initial fetch in getEmployeeDetails for clocking record succeeded.
            // If fetchedData.data.latestClockIn.clockInTime was populated by getEmployeeDetails, use it.
            // This part relies on getEmployeeDetails being updated to store this if needed, or the direct fetch succeeded.
            // The current getEmployeeDetails fetches general details, and then tries to fetch latest-clocking.
            // If that latest-clocking fetch was successful and populated `timeOnSite` initially, this interval can use it.
             if (timeOnSite && timeOnSite !== "N/A" && timeOnSite !== "" && data?.latestClockIn?.clockInTime) {
                 //This condition means timeOnSite was set by the fetch in getEmployeeDetails
                 effectiveClockInTime = data.latestClockIn.clockInTime; // use the one fetched
                 intervalId = setInterval(() => {
                    const timeOnSiteResult = calculateTimeOnSite(effectiveClockInTime);
                    setTimeOnSite(timeOnSiteResult.formatted);
                }, 60000);
             }
        }

        return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }, [data, location.state, timeOnSite, activeBreaks]); // timeOnSite and activeBreaks are added to dependencies to re-evaluate if they change from N/A

    // Effect to determine current active break
    useEffect(() => {
        const ongoingBreak = activeBreaks.find(br => !br.breakEndTime && br.status === 'ongoing');
        setCurrentActiveBreakDetails(ongoingBreak || null);
    }, [activeBreaks]);

    const handleBreakButtonClick = async () => {
        if (!data?.latestClockIn?._id) {
            console.error("No active clock-in session found to start/end break.");
            toast.error("Cannot start/end break: No active clock-in session."); // User-facing error
            return;
        }
        setIsBreakButtonLoading(true);
        try {
            if (currentActiveBreakDetails) {
                // End current break
                const responseData = await api.shiftActivities.endBreak(currentActiveBreakDetails._id);
                if (!responseData.success) {
                    throw new Error(responseData.message || 'Failed to end break');
                }
                toast.success(responseData.message || 'Break ended successfully!');
                // After ending break, navigate to main page
                navigate('/'); 
                // No need to explicitly call fetchBreaksForClocking here if navigating away
            } else {
                // Start a new break
                const responseData = await api.shiftActivities.startBreak(data.latestClockIn._id, id);
                if (!responseData.success) {
                    throw new Error(responseData.message || 'Failed to start break');
                }
                toast.success(responseData.message || 'Break started successfully!');
                navigate('/'); // Navigate to main page
            }
        } catch (error) {
            console.error("Error handling break action:", error);
            toast.error(error.message || "An error occurred handling break action."); 
        } finally {
            setIsBreakButtonLoading(false);
        }
    };

    return (
        <Box height="100vh" display="flex" flexDirection="column" backgroundColor={colors.primary.special} padding={{ xs: 1, sm: 1.5, md: 2 }}>
            <Toaster position="top-center" reverseOrder={false}/>
            {/* Header Section */}
            <Box 
                display="flex" 
                justifyContent="space-between"
                alignItems="center" 
                backgroundColor={colors.primary.teal}
                paddingX={{ xs: 1, sm: 1.5, md: 2 }}
                paddingY={0.5}
            >
                <Button 
                    variant="text" 
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/")} 
                    sx={{
                        color: colors.text.white,
                        textTransform: 'none',
                        fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                        mr: { xs: 1, sm: 1.5, md: 2 },
                        padding: { xs: '4px 8px', sm: '6px 12px', md: '8px 16px' },
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    Exit
                </Button>
                <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <Header title="My Profile" />
                </Box>
                <Box sx={{ minWidth: 80, visibility: 'hidden' }} /> 
            </Box>

            {/* Main Content */}
            <Box flex={1} display="flex" padding={{ xs: "8px 0", sm: "10px 0", md: "10px 0" }} gap={{ xs: 1, sm: 1.5, md: 2 }}>
                {/* Left Panel */}
                <Box width={{ xs: "100%", md: "25%" }} display="flex" flexDirection="column" gap={{ xs: 1, sm: 1.5, md: 2 }}>
                    <Box
                        flex={1}
                        width="100%"
                        borderRadius="10px"
                        backgroundColor={colors.grey.lighter}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                        gap={{ xs: 1, sm: 1.5, md: 2 }}
                        padding={{ xs: 1, sm: 1.5, md: 2 }}
                    >
                        <UserImage />
                        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' } }}>{`${data?.names.firstName} ${data?.names.lastName}`}</Typography>
                        <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>{data?.location || "No Location"}</Typography>
                        <Typography variant="h5" fontWeight="bold" color="green" sx={{ fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' }, textAlign: 'center', px: 1 }}>
                            {getShiftStatus(data?.shifts)}
                        </Typography>
                    </Box>
                    <Box
                        flex={0.3}
                        borderRadius="10px"
                        textAlign="left"
                        padding={{ xs: 1, sm: 1.5, md: 2 }}
                        backgroundColor={colors.grey.lighter}
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                    >
                        <Box display="flex" justifyContent="space-between" width="100%" my={{ xs: 0.3, sm: 0.4, md: 0.5 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>Time on Site</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>{timeOnSite}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" width="100%" my={{ xs: 0.3, sm: 0.4, md: 0.5 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>Time spent working</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>{timeWorkedFormatted}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" width="100%" my={{ xs: 0.3, sm: 0.4, md: 0.5 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>Total break time</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>{totalBreakTimeFormatted}</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Middle Panel */}
                <Box flex={1} display="flex" flexDirection="column" gap={2}>
                    {/* Bulletin */}
                    <Box
                        flex={0.3}
                        borderRadius="10px"
                        textAlign="center"
                        backgroundColor={colors.grey.lighter}
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                    >
                        <Typography>Bulletin</Typography>
                        <Typography>Today is Holiday</Typography>
                    </Box>

                    {/* Empty */}
                    <Box display="none" />

                    {/* Button */}
                    <Box
                        borderRadius="10px"
                        backgroundColor="transparent"
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        marginTop="auto"
                        gap={{ xs: 1, sm: 1.5, md: 2 }}
                    >
                        <Box border="none"> {/* take or end break button */}
                            <Button 
                                sx={{
                                    width: "100%", 
                                    padding: { xs: "8px", sm: "10px", md: "10px" }, 
                                    backgroundColor: currentActiveBreakDetails ? colors.secondary.yellow : colors.grey.lighter, 
                                    color: currentActiveBreakDetails ? colors.text.black : colors.primary.teal, 
                                    border: `2px solid ${currentActiveBreakDetails ? colors.secondary.yellow : colors.primary.teal}`,
                                    fontWeight: "bold",
                                    fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                                    '&:hover': {
                                        backgroundColor: currentActiveBreakDetails ? colors.secondary.darkYellow : colors.grey.light,
                                    }
                                }}
                                onClick={handleBreakButtonClick}
                                disabled={isBreakButtonLoading || !data?.latestClockIn?._id}
                            >
                                {isBreakButtonLoading 
                                    ? "Processing..." 
                                    : (currentActiveBreakDetails ? "End Break" : "Start Break")}
                            </Button>
                        </Box>
                        <Box border="none"> {/* end shift button */} 
                            <Button 
                                variant="contained"
                                sx={{ 
                                    width: "100%", 
                                    padding: { xs: "8px", sm: "10px", md: "10px" }, 
                                    backgroundColor: colors.primary.teal,
                                    color: colors.text.grey,
                                    fontWeight: "bold",
                                    fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" }
                                }}
                                onClick={() => navigate(`/ClockOutPin/${id}`)}
                                disabled={isBreakButtonLoading}
                            >
                                End Shift
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Right Panel */}
                <Box width={{ xs: "100%", md: "25%" }} display="flex" flexDirection="column" gap={{ xs: 1, sm: 1.5, md: 2 }}>
                    <Box
                        width="100%"
                        height={{ xs: "80px", sm: "90px", md: "100px" }}
                        borderRadius="10px"
                        backgroundColor={colors.grey.lighter}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        padding={{ xs: 0.5, sm: 1, md: 1 }}
                    >
                        <RealTimeClock />
                    </Box>
                    <Box
                        flex={1}
                        borderRadius="10px"
                        textAlign="center"
                        backgroundColor={colors.grey.lighter}
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                        padding={{ xs: 1, sm: 1.5, md: 2 }}
                    >
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>Shift Start</Typography>
                        <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } }}>Today is Holiday</Typography>
                    </Box>
                    <Box
                        height={{ xs: "180px", sm: "220px", md: "250px" }}
                        borderRadius="10px"
                        backgroundColor={colors.grey.lighter}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                        gap={{ xs: 1, sm: 1.5, md: 2 }}
                        padding={{ xs: 1, sm: 1.5, md: 2 }}
                    >
                        <Typography variant="h5" sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>Shift Controls</Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default EmployeeClockedIn;
